export const parseOSVersion = (osVersion) => {
	const tempOSVersion = osVersion || 'N/A';
	const osVersionNumbers = [];
	
	for (let number of tempOSVersion.split('.')) {
		osVersionNumbers.push(number);
	}

	return osVersionNumbers.slice(0, 3).join('.');
};

export const getOSVersion = (osVersion) => {
	const OSNumber = !!osVersion && osVersion.split('.')[0];
	return OSNumber ? `os${OSNumber}` : null;
};

const OS_VERSION_SEGMENTS_TO_COMPARE = 3;

const SEGMENT_LEADING_DIGITS_PATTERN = /^\d+/;

// A segment may carry a build/pre-release suffix ('2003_alpha', '5-rc1') - that is a real fleet
// version, not garbage, so it compares on its leading digits with the suffix stripped. Whether it
// had one is remembered too: a suffixed segment marks the whole version as a pre-release, which
// must sort below the same version without one, mirroring how rest-api's
// packaging.version.parse treats '6.0.5_alpha' as '6.0.5a0' - less than plain '6.0.5'. A segment
// with no leading digit at all ('abc', 'x') is genuine garbage and stays unknown.
const toComparableSegment = (segment) => {
	const leadingDigits = SEGMENT_LEADING_DIGITS_PATTERN.exec(segment);

	if (!leadingDigits) {
		return null;
	}

	return { value: Number(leadingDigits[0]), isPreRelease: leadingDigits[0].length < segment.length };
};

// Only the first 3 segments (major.minor.patch) carry version meaning; the 4th segment is a
// build number that must not affect the comparison. A version whose first segment is not a
// number is unknown, so this returns `null` and the comparison below returns null consistently
// rather than parsing a garbage number.
const toComparableSegments = (osVersion) => {
	if (!osVersion || osVersion === 'N/A') {
		return null;
	}

	const segments = String(osVersion).split('.').slice(0, OS_VERSION_SEGMENTS_TO_COMPARE);

	while (segments.length < OS_VERSION_SEGMENTS_TO_COMPARE) {
		segments.push('0');
	}

	const comparableSegments = segments.map(toComparableSegment);

	if (comparableSegments.some((segment) => { return segment === null; })) {
		return null;
	}

	return {
		values: comparableSegments.map((segment) => { return segment.value; }),
		isPreRelease: comparableSegments.some((segment) => { return segment.isPreRelease; }),
	};
};

// Numeric, segment-by-segment comparison: '10.0.0' must sort above '9.9.9', which a lexicographic
// string comparison would get wrong. Returns null when either version is unknown, so callers can
// treat "unknown" as "does not satisfy the predicate" instead of guessing an ordering. When every
// numeric segment is equal, a pre-release build still sorts below the same non-pre-release version.
const compareOSVersions = (osVersion, otherOSVersion) => {
	const version = toComparableSegments(osVersion);
	const otherVersion = toComparableSegments(otherOSVersion);

	if (!version || !otherVersion) {
		return null;
	}

	for (let segmentIndex = 0; segmentIndex < OS_VERSION_SEGMENTS_TO_COMPARE; segmentIndex++) {
		if (version.values[segmentIndex] !== otherVersion.values[segmentIndex]) {
			return version.values[segmentIndex] - otherVersion.values[segmentIndex];
		}
	}

	if (version.isPreRelease !== otherVersion.isPreRelease) {
		return version.isPreRelease ? -1 : 1;
	}

	return 0;
};

export const isOSVersionAtLeast = (osVersion, minimumVersion) => {
	const comparison = compareOSVersions(osVersion, minimumVersion);

	return comparison !== null && comparison >= 0;
};

export const isOSVersionBelow = (osVersion, referenceVersion) => {
	const comparison = compareOSVersions(osVersion, referenceVersion);

	return comparison !== null && comparison < 0;
};
